/**
 * useCatalogModels — one hook every model picker uses to turn a credential into
 * its selectable model list.
 *
 * Models now come from the synced model_catalog (GET /evals/model-catalog),
 * keyed by the credential's provider — the user no longer maintains a per-key
 * model list. The two exceptions have no catalog and keep their user-entered
 * config.models[]: Azure (deployment names) and openai_compat (arbitrary
 * endpoint).
 *
 * Usage:
 *   const modelsForCredential = useCatalogModels(teamId, integrations);
 *   const options = modelsForCredential(selectedCredential); // string[] of ids
 *
 * It prefetches the catalog once per distinct provider present in `integrations`
 * and caches it, so switching between credentials is instant. Fail-open: on any
 * error (or while loading) it falls back to the credential's own config.models[]
 * so a picker is never empty when it doesn't have to be.
 */
import { useState, useEffect, useCallback } from 'react';
import { listModelCatalog } from 'features/evals/api/evalsApi';

const MANUAL_MODEL_PROVIDERS = new Set(['azure_openai', 'openai_compat']);

// Decision models (Jev, Trodo) answer questions, they do not chat: never offer
// them in a chat model picker even if one slips into `integrations`.
const DECISION_PROVIDERS = new Set(['jev', 'trodo']);

/**
 * Module-level catalog cache, keyed by provider.
 *
 * The docblock above says this hook "prefetches the catalog once per distinct
 * provider and caches it" — but the cache was component state, so every mount
 * refetched every provider. Opening the eval editor issued seven
 * GET /evals/model-catalog calls, one per connected provider, and issued them
 * again on the next mount, and again in every other model picker across
 * Agents and Playground.
 *
 * The catalog is genuinely global — the endpoint's query ignores the team and
 * the contents only change when the model-catalog sync job runs — so a
 * per-session cache is the right lifetime. Promises are stored rather than
 * results so the seven concurrent first calls, or two pickers mounting
 * together, share one request each instead of racing.
 */
const _catalogCache = new Map(); // provider → Promise<{ models, profile }>

function fetchCatalog(teamId, provider) {
  const hit = _catalogCache.get(provider);
  if (hit) return hit;
  const p = listModelCatalog(teamId, provider)
    .then((d) => ({ models: Array.isArray(d?.models) ? d.models : [], profile: d?.profile || null }))
    .catch((err) => {
      // Never cache a failure — the next picker should retry.
      _catalogCache.delete(provider);
      throw err;
    });
  _catalogCache.set(provider, p);
  return p;
}

/** Drop the cached catalog (after a provider sync, or on sign-out). */
export function clearModelCatalogCache() { _catalogCache.clear(); }

export function useCatalogModels(teamId, integrations) {
  const [byProvider, setByProvider] = useState({});

  // Stable dependency: the sorted, distinct set of catalog-backed providers in play.
  const providerKey = [
    ...new Set((integrations || []).map((i) => i && i.provider).filter(Boolean)),
  ]
    .filter((p) => !MANUAL_MODEL_PROVIDERS.has(p) && !DECISION_PROVIDERS.has(p))
    .sort()
    .join(',');

  useEffect(() => {
    // teamId may be null — Agents/Playground resolve the team from
    // the JWT default + x-team-id header, and the catalog is global anyway. Only
    // gate on having providers to fetch.
    if (!providerKey) return undefined;
    const providers = providerKey.split(',');
    let cancelled = false;
    Promise.all(
      providers.map(async (p) => {
        try {
          return [p, (await fetchCatalog(teamId, p)).models];
        } catch (_) {
          return [p, []];
        }
      }),
    ).then((pairs) => {
      if (!cancelled) setByProvider(Object.fromEntries(pairs));
    });
    return () => { cancelled = true; };
  }, [teamId, providerKey]);

  /** credential → array of selectable model-id strings. */
  return useCallback(
    (cred) => {
      if (!cred || DECISION_PROVIDERS.has(cred.provider)) return [];
      if (MANUAL_MODEL_PROVIDERS.has(cred.provider)) return cred.config?.models || [];
      const cat = byProvider[cred.provider];
      if (cat && cat.length) return cat.map((m) => m.id);
      return cred.config?.models || []; // fallback while loading / empty catalog
    },
    [byProvider],
  );
}

/** Full catalog rows (with capability flags) for one provider, or [] until loaded. */
export function useProviderCatalog(teamId, provider) {
  const [models, setModels] = useState([]);
  useEffect(() => {
    if (!provider || MANUAL_MODEL_PROVIDERS.has(provider)) { setModels([]); return undefined; }
    let cancelled = false;
    fetchCatalog(teamId, provider)
      .then((d) => { if (!cancelled) setModels(d.models); })
      .catch(() => { if (!cancelled) setModels([]); });
    return () => { cancelled = true; };
  }, [teamId, provider]);
  return models;
}

export { MANUAL_MODEL_PROVIDERS, DECISION_PROVIDERS };
